import { Calendar as CalendarIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { DrawerContent } from './drawer-content';
import { DATE_TRIGGER_CLASS } from './form-control-style';
import { MonthGrid } from './month-grid';
import { useIsCompact } from './use-is-compact';

export interface MonthFieldProps {
  /** 트리거 id — `FieldLabel htmlFor` 와 연결(접근성). */
  id?: string;
  /**
   * 필드명(= `FieldLabel` 문구) — **compact 드로워 헤더**로 쓰인다. 생략 시 기본 문구로 폴백.
   * (`SelectField.label`·`DateField.label` 과 동일 관행.)
   */
  label?: string;
  /** 선택값 `yyyy-MM`. */
  value: string;
  /** 선택 시 `yyyy-MM` 로 통지. */
  onChange: (yyyymm: string) => void;
  /** 선택 가능 하한 `yyyy-MM`(이전 비활성). */
  min?: string;
  /** 선택 가능 상한 `yyyy-MM`(이후 비활성). */
  max?: string;
  /** placeholder(미선택 시 트리거 텍스트). */
  placeholder?: string;
}

// 입출력은 `yyyy-MM`(하이픈). `DateField`(일 단위)의 월 단위 카운터파트 — 날짜 기준이 '월별'일 때 사용.
const ISO_MONTH = /^\d{4}-\d{2}$/;

/**
 * 월 선택 — 연도 네비게이션 + 12개월 그리드. `yyyy-MM` 문자열을 입출력하고 트리거에 `yyyy-MM` 을 표시한다.
 * `DateField`(일 단위)의 월 단위 카운터파트(통계 날짜기준 '월별'). native `<input type=month>`(브라우저 기본
 * UI·로케일 차이) 대체. 라벨은 호출처가 `Field`/`FieldLabel` 로 연결.
 *
 * **표면은 반응형**(경계 `lg`) — pc 는 `Popover`, 비-pc 는 bottom `Drawer`. 그리드 본체는 두 표면이 공유한다.
 * compact 에선 그리드가 넓어져 칸이 커지므로 터치 타깃이 자연히 확보된다. (ADR ⑤-1.)
 */
export function MonthField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  placeholder = UI_TEXT.DATE_PLACEHOLDER,
}: MonthFieldProps) {
  const isCompact = useIsCompact();
  const [open, setOpen] = useState(false);
  const selected = ISO_MONTH.test(value) ? value : '';
  const [viewYear, setViewYear] = useState(() =>
    selected ? Number(selected.slice(0, 4)) : new Date().getFullYear(),
  );

  // 열 때 선택값 연도로 뷰 동기화(다른 연도 보다가 닫았어도 재진입 시 선택 월 노출).
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && selected) setViewYear(Number(selected.slice(0, 4)));
  };

  const triggerBody: ReactNode = (
    <>
      <span>{selected || placeholder}</span>
      <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
    </>
  );

  const grid = (
    <MonthGrid
      year={viewYear}
      onYearChange={setViewYear}
      selected={selected || undefined}
      min={min}
      max={max}
      onSelect={(ym) => {
        onChange(ym);
        setOpen(false);
      }}
    />
  );

  if (isCompact) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange} direction="bottom">
        <DrawerTrigger
          id={id}
          className={DATE_TRIGGER_CLASS}
          data-placeholder={selected ? undefined : ''}
        >
          {triggerBody}
        </DrawerTrigger>
        <DrawerContent aria-describedby={undefined} className="gap-0 overflow-hidden">
          <DrawerHeader className="shrink-0 border-b border-border">
            <DrawerTitle className="text-base font-semibold">
              {label ?? UI_TEXT.DATE_PLACEHOLDER}
            </DrawerTitle>
          </DrawerHeader>
          {/* 그리드는 폭을 채운다(달력과 달리 3열이라 넓어질수록 터치 타깃이 커짐 — 늘리는 게 이득). */}
          <div className="p-4">{grid}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        id={id}
        className={DATE_TRIGGER_CLASS}
        data-placeholder={selected ? undefined : ''}
      >
        {triggerBody}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 bg-card" align="start">
        {grid}
      </PopoverContent>
    </Popover>
  );
}
